/**
 * @file inward-access.ts
 * @module app/modules/inward/utils/inward-access
 * @description Who can do what in the Inward module. Mirrors the backend matrix in
 *   `backend/src/_platforms/_admin/constants/inward.constants.ts` and
 *   `_admin/middleware/inwardAccess.ts`, so the UI never offers an action the API rejects.
 *
 *   Deliberately does not use `Method.hasPermission`, which returns true for any user with
 *   a non-empty `role` list.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import {
  Admin,
  PermissionModule,
  Permissions,
} from "../../../../utils/constants";

export enum InwardAction {
  View = "view",
  /** Also requires being the PO's assigned loading bay manager (checked per PO). */
  CreateGRN = "createGRN",
  SaveQuantities = "saveQuantities",
  AddBatch = "addBatch",
  WarehouseApprove = "warehouseApprove",
  FinanceApprove = "financeApprove",
  AssignLoadingBay = "assignLoadingBay",
  AcknowledgeCostPrice = "acknowledgeCostPrice",
  DownloadReport = "downloadReport",
  /** tick / untick "EFD verified" in the report */
  VerifyEfd = "verifyEfd",
  /** reject a whole GRN (own right 77) */
  RejectGrn = "rejectGrn",
  /** approve / reject a GRN's missing / damaged (Warehouse or Finance approver) */
  DecideShortfall = "decideShortfall",
}

const ACTION_PERMISSIONS: Readonly<Record<InwardAction, readonly number[]>> = {
  [InwardAction.View]: [
    Permissions.View,
    Permissions.InwardLoadingBayManager,
    Permissions.InwardCreateGRN,
    Permissions.InwardWarehouseApprove,
    Permissions.InwardFinanceApprove,
    Permissions.InwardAssignLoadingBay,
    Permissions.InwardAcknowledgeCostPrice,
    Permissions.InwardVerifyEfd,
    Permissions.InwardRejectGrn,
    Permissions.Download,
  ],
  [InwardAction.CreateGRN]: [Permissions.InwardLoadingBayManager],
  [InwardAction.SaveQuantities]: [Permissions.Add],
  [InwardAction.AddBatch]: [Permissions.Edit],
  [InwardAction.WarehouseApprove]: [Permissions.InwardWarehouseApprove],
  [InwardAction.FinanceApprove]: [Permissions.InwardFinanceApprove],
  [InwardAction.AssignLoadingBay]: [Permissions.InwardAssignLoadingBay],
  [InwardAction.AcknowledgeCostPrice]: [Permissions.InwardAcknowledgeCostPrice],
  [InwardAction.DownloadReport]: [Permissions.Download],
  [InwardAction.VerifyEfd]: [Permissions.InwardVerifyEfd],
  [InwardAction.RejectGrn]: [Permissions.InwardRejectGrn],
  [InwardAction.DecideShortfall]: [
    Permissions.InwardWarehouseApprove,
    Permissions.InwardFinanceApprove,
  ],
};

interface RoleModulePermission {
  module?: number;
  permissions?: number[];
}

/** Fields of the logged-in admin user that access depends on. */
export interface InwardUser {
  _id?: string;
  userType?: number;
  roleAndPermission?: {
    isAdminRole?: boolean;
    permissions?: RoleModulePermission[];
  };
}

/** Super admin (userType Admin or the built-in admin role): everything is open. */
export const isInwardSuperAdmin = (user?: InwardUser | null): boolean =>
  !!user &&
  (user.userType === Admin || user.roleAndPermission?.isAdminRole === true);

export const canPerformInwardAction = (
  user: InwardUser | null | undefined,
  action: InwardAction
): boolean => {
  if (!user) return false;
  if (isInwardSuperAdmin(user)) return true;

  const allowed = [
    Permissions.AllPermissions,
    ...(ACTION_PERMISSIONS[action] ?? []),
  ];
  return (user.roleAndPermission?.permissions ?? []).some(
    (entry) =>
      (entry?.module === PermissionModule.Inward ||
        entry?.module === PermissionModule.AllModules) &&
      (entry?.permissions ?? []).some((p) => allowed.includes(p))
  );
};

/**
 * A loading bay manager only (cannot assign, not super admin): the PO list shows only their own
 * POs planned for the chosen day (the API enforces the same).
 */
export const isLoadingBayManagerOnly = (
  user: InwardUser | null | undefined
): boolean =>
  !!user &&
  !isInwardSuperAdmin(user) &&
  !canPerformInwardAction(user, InwardAction.AssignLoadingBay);

/** Assigned loading bay manager of a PO (or super admin) may start inward and create its GRN. */
export const canDoGrnForPo = (
  user: InwardUser | null | undefined,
  assignment?: { loadingBayManager?: { reference?: unknown } } | null
): boolean => {
  if (!user) return false;
  if (isInwardSuperAdmin(user)) return true;
  return (
    !!assignment?.loadingBayManager?.reference &&
    String(assignment.loadingBayManager.reference) === String(user._id) &&
    canPerformInwardAction(user, InwardAction.CreateGRN)
  );
};
